import { useFetchPage } from "~/core/composables/useFetchPage";
import { useSearch } from "~/core/composables/useSearch";
import { useSort } from "~/core/composables/useSort";
import type { UseFetchParameters } from "~/core/models/UseFetchParameters.type";
import type { UseFetchResponse } from "~/core/models/UseFetchResponse.interface";
import filtersUtils from "~/core/utils/filters.utils";
import type { Fleet } from "~/modules/fleet/models/Fleet.interface";
import FleetService from "~/modules/fleet/services/fleet.service";
import type { FiltersSelection } from "~/types";
import { Filters, FiltersType, OPERATOR_RSQL_ILIKE } from "~/types";

interface UseFetchFleetsResponse extends UseFetchResponse<Fleet> {
  setCompatibilityCriteria: (
    compatibilityCriteria?: string | undefined,
  ) => void;
}

export function useFetchFleets({
  organizationCode,
  searchMode,
  useQueryParametersForPagination,
  compatibilityCriteria,
}: UseFetchParameters): UseFetchFleetsResponse {
  const service = FleetService.getOrganizationFleets.bind(FleetService);

  const appliedFilters: FiltersSelection = new Map([
    [
      Filters.FLEET_NAME,
      {
        key: "friendlyName",
        operator: OPERATOR_RSQL_ILIKE,
        value: "",
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
  ]);

  const { searchParams, filters, setFilter, search, onSearch } = useSearch(
    FiltersType.RSQL,
    appliedFilters,
    searchMode,
  );
  const { sort, onSortChange } = useSort();
  const { isLoading, results, error, fetchData } = useFetchPage({
    organizationCode,
    service,
    searchParams,
    sort,
    sortOverrideCallback: search,
    useQueryParametersForPagination,
  });

  const localeCompatibilityCriteria = ref(compatibilityCriteria);

  function setCompatibilityCriteria(compatibilityCriteria?: string) {
    localeCompatibilityCriteria.value = compatibilityCriteria;
  }

  async function fetchDataWithCompatibilityCriteria(
    page?: number,
    pageSize?: number,
    params?: Map<string, unknown>,
  ) {
    if (localeCompatibilityCriteria.value) {
      if (!params) {
        params = new Map();
      }
      params.set("compatibilityCriteria", localeCompatibilityCriteria.value);
    }

    return await fetchData(page, pageSize, params);
  }

  return {
    isLoading,
    error,
    results,
    filters,
    fetchData: fetchDataWithCompatibilityCriteria,
    setCompatibilityCriteria,
    setFilter,
    onSortChange,
    search,
    onSearch,
    sort,
  };
}
